import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import {MatSelect, MatSelectChange} from "@angular/material/select";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";

export interface SelectType
{
	label: String;
	value: any;
}

/** Number of options to show per page */
const PAGE_SIZE = 20;

@Component({
	selector: 'custom-ext-select',
	templateUrl: './custom-ext-select.component.html',
	styleUrls: ['./custom-ext-select.component.scss']
})
export class CustomExtSelectComponent implements OnChanges, OnDestroy
{
	@Input() label: String | undefined;
	@Input() placeholder: String | undefined;
	@Input() values?: any[];
	@Input() defaultValue: any;
	@Input() disabled: boolean | undefined = false;
	@Output() defaultValueChange: EventEmitter<any> = new EventEmitter<any>();
	@Input() columnName?: string;
	@Output() columnChange: EventEmitter<string> = new EventEmitter<string>();
	@Input() extraClass: string = '';
	@Input() preventIdChange: boolean = false;
	@Input() searchable: boolean = true;
	@Input() useTranslation: boolean = false;
	@Input() translationPrefix: string = "";

	@ViewChild('selectElement') selectElement: MatSelect | undefined;
	@ViewChild('searchInput') searchInput: ElementRef | undefined;

	/** Current search input value */
	searchValue: string = '';

	/** Filtered and limited options for rendering */
	renderedOptions: any[] = [];

	/** Selected option not in current page (kept in DOM for MatSelect to show label) */
	hiddenSelectedOption: any | null = null;

	/** Total count of filtered options (before pagination) */
	totalFilteredCount: number = 0;

	/** Current page (1-based) */
	currentPage: number = 1;

	/** Total number of pages */
	totalPages: number = 1;

	/** Page size */
	readonly pageSize = PAGE_SIZE;

	/** Cached filtered options for pagination */
	private currentFilteredOptions: any[] = [];

	/** Subject for search input with debounce */
	private readonly searchSubject = new Subject<string>();

	/** Subject for component destruction cleanup */
	private readonly destroy$ = new Subject<void>();

	constructor()
	{
		this.setupSearchDebounce();
	}

	ngOnChanges(changes: SimpleChanges): void
	{
		if (changes['values'] && Array.isArray(changes['values'].currentValue))
		{
			this.currentPage = 1;
			this.applyFilter(this.searchValue);
		}
		else if (changes['defaultValue'])
		{
			// Update hidden option when selected value changes from parent
			this.updateRenderedOptions();
		}
	}

	ngOnDestroy(): void
	{
		this.destroy$.next();
		this.destroy$.complete();
		this.searchSubject.complete();
	}

	/**
	 * Sets up debounced search to avoid filtering on every keystroke
	 */
	private setupSearchDebounce(): void
	{
		this.searchSubject.pipe(
			debounceTime(150),
			distinctUntilChanged(),
			takeUntil(this.destroy$)
		).subscribe(searchTerm =>
		{
			this.currentPage = 1;
			this.applyFilter(searchTerm);
		});
	}

	/**
	 * Applies filter to options and calculates pagination.
	 */
	private applyFilter(searchTerm: string): void
	{
		const normalizedTerm = (searchTerm || '').trim().toUpperCase();

		if (!normalizedTerm)
		{
			this.currentFilteredOptions = this.values ? [...this.values] : [];
		}
		else
		{
			this.currentFilteredOptions = (this.values || []).filter(option =>
			{
				const id = String(option.id).toUpperCase();
				const name = String(option.name).toUpperCase();
				return id.includes(normalizedTerm) || name.includes(normalizedTerm);
			});
		}

		this.totalFilteredCount = this.currentFilteredOptions.length;
		this.totalPages = Math.max(1, Math.ceil(this.totalFilteredCount / PAGE_SIZE));

		if (this.currentPage > this.totalPages)
		{
			this.currentPage = this.totalPages;
		}

		this.updateRenderedOptions();
	}

	/**
	 * Updates rendered options based on current page.
	 * Keeps the selected option in DOM (hidden) if not on current page,
	 * so MatSelect can display its label.
	 */
	private updateRenderedOptions(): void
	{
		const startIndex = (this.currentPage - 1) * PAGE_SIZE;
		const endIndex = startIndex + PAGE_SIZE;
		this.renderedOptions = this.currentFilteredOptions.slice(startIndex, endIndex);

		const renderedIds = new Set(this.renderedOptions.map(opt => opt.id));
		if (this.defaultValue != null && !renderedIds.has(this.defaultValue))
		{
			this.hiddenSelectedOption = (this.values || []).find(opt => opt.id == this.defaultValue) || null;
		}
		else
		{
			this.hiddenSelectedOption = null;
		}
	}

	/** Navigate to first page */
	goToFirstPage(event: MouseEvent): void
	{
		event.stopPropagation();
		if (this.currentPage !== 1)
		{
			this.currentPage = 1;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
		}
	}

	/** Navigate to previous page */
	goToPreviousPage(event: MouseEvent): void
	{
		event.stopPropagation();
		if (this.currentPage > 1)
		{
			this.currentPage--;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
		}
	}

	/** Navigate to next page */
	goToNextPage(event: MouseEvent): void
	{
		event.stopPropagation();
		if (this.currentPage < this.totalPages)
		{
			this.currentPage++;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
		}
	}

	/** Navigate to last page */
	goToLastPage(event: MouseEvent): void
	{
		event.stopPropagation();
		if (this.currentPage !== this.totalPages)
		{
			this.currentPage = this.totalPages;
			this.updateRenderedOptions();
			this.scrollPanelToTop();
		}
	}

	private scrollPanelToTop(): void
	{
		setTimeout(() =>
		{
			const panel = this.selectElement?.panel?.nativeElement as HTMLElement;
			if (panel)
			{
				panel.scrollTop = 0;
			}
		});
	}

	/** Handles search input changes with debounce */
	onSearchValueChange(event: any): void
	{
		this.searchValue = event || '';
		this.searchSubject.next(this.searchValue);
	}

	/** Clears the search input and resets filter */
	clearSearch(): void
	{
		this.searchValue = '';
		this.searchSubject.next('');
	}

	onChange(event: MatSelectChange): void
	{
		const value = event.value;
		if (!this.preventIdChange && this.values)
		{
			this.defaultValue = this.values.find(elem => elem.id == value);
		}
		// Update hidden option visibility after selection
		this.updateRenderedOptions();
		this.defaultValueChange.emit(value);
		this.columnChange.emit(this.columnName ?? '');
	}

	openedChange(isOpen: boolean): void
	{
		if (isOpen)
		{
			this.currentPage = 1;
			this.applyFilter(this.searchValue);
			this.scrollPanelToTop();
			setTimeout(() =>
			{
				this.searchInput?.nativeElement?.focus();
			});
		}
		else
		{
			if (this.searchValue)
			{
				this.clearSearch();
			}
		}
	}

	/** TrackBy function for ngFor optimization */
	trackByOptionId(index: number, option: any): any
	{
		return option.id;
	}

	compare(x: any, y: any): boolean {
		// Usa l'uguaglianza debole (==) invece di quella stretta (===)
		return x == y;
	}
}
